import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useSmoothScroll";

const LINKS = [
  { key: "home", to: "/" },
  { key: "services", to: "/services" },
  { key: "library", to: "/library" },
  { key: "journal", to: "/journal" },
  { key: "about", to: "/about" },
];

export default function Nav() {
  const { lang, set, t } = useLang();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      setHidden(y > last && y > 300 && !open);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  useEffect(() => setOpen(false), [pathname]);

  const dark = pathname === "/" && !scrolled;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-transform duration-500 ${
          hidden ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div
          className={`transition-colors duration-500 ${
            scrolled ? "bg-paper/80 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
          }`}
        >
          <div
            className={`flex items-center justify-between px-6 md:px-12 lg:px-20 h-[74px] transition-colors duration-500 ${
              dark ? "text-paper" : "text-ink"
            }`}
          >
            <div aria-hidden="true" className="w-6" />

            <div className="flex items-center gap-5 md:gap-7">
              <div className={`flex items-center gap-1 eyebrow pr-4 md:pr-6 border-r ${dark ? "border-paper/25" : "border-ink/15"}`}>
                <button
                  data-testid="lang-ro"
                  onClick={() => set("ro")}
                  className={lang === "ro" ? "opacity-100" : "opacity-50 hover:opacity-90"}
                >
                  RO
                </button>
                <span className="opacity-25">/</span>
                <button
                  data-testid="lang-en"
                  onClick={() => set("en")}
                  className={lang === "en" ? "opacity-100" : "opacity-50 hover:opacity-90"}
                >
                  EN
                </button>
              </div>
              <button
                data-testid="nav-menu-toggle"
                data-cursor="link"
                onClick={() => setOpen(true)}
                aria-label={lang === "ro" ? "Deschide meniul" : "Open menu"}
                className="group inline-flex items-center gap-2.5 eyebrow"
              >
                <span className="hidden sm:inline">{lang === "ro" ? "Meniu" : "Menu"}</span>
                <span className="flex flex-col items-end gap-[5px]" aria-hidden="true">
                  <span className="block h-px w-6 bg-current transition-all duration-300 group-hover:w-4" />
                  <span className="block h-px w-6 bg-current transition-all duration-300" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="nav-overlay"
            className="fixed inset-0 z-50 bg-noir text-paper"
          >
            <div className="flex items-center justify-between px-6 md:px-12 lg:px-20 h-[74px]">
              <span className="display text-xl">
                Oana <span className="serif italic font-normal">Mudura</span>
              </span>
              <button
                data-testid="nav-menu-close"
                data-cursor="link"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 eyebrow"
              >
                {lang === "ro" ? "Închide" : "Close"} <X size={22} />
              </button>
            </div>

            <nav className="flex flex-col px-6 md:px-12 lg:px-20 mt-6 md:mt-10">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.key}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mnav-${l.key}`}
                  data-cursor="link"
                  className="group display text-4xl sm:text-5xl md:text-6xl py-2.5 md:py-3.5 border-b border-white/15 flex items-baseline gap-4 md:gap-6 transition-[padding] duration-500 hover:pl-3"
                >
                  <span className="eyebrow text-white/40 tnum">0{i + 1}</span>
                  <span className="opacity-90 transition-opacity duration-300 group-hover:opacity-100">{t.nav[l.key]}</span>
                </NavLink>
              ))}
              <button
                data-testid="mnav-contact"
                data-cursor="link"
                onClick={() => { setOpen(false); setTimeout(() => scrollToId("#contact"), 60); }}
                className="group display text-4xl sm:text-5xl md:text-6xl py-2.5 md:py-3.5 text-left flex items-baseline gap-4 md:gap-6 transition-[padding] duration-500 hover:pl-3"
              >
                <span className="eyebrow text-white/40 tnum">0{LINKS.length + 1}</span>
                {t.contact.label}
              </button>
            </nav>

            <div className="absolute bottom-8 left-6 md:left-12 lg:left-20 flex gap-2 eyebrow">
              <button data-testid="mnav-lang-ro" onClick={() => set("ro")} className={lang === "ro" ? "" : "opacity-40 hover:opacity-80"}>RO</button>
              <span className="opacity-25">/</span>
              <button data-testid="mnav-lang-en" onClick={() => set("en")} className={lang === "en" ? "" : "opacity-40 hover:opacity-80"}>EN</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
